import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { TablePagination } from '@/components/ui/table-pagination';
import { Download } from 'lucide-react';

interface MergedTradeRow {
  timestamp: string;
  bot_name: string;
  signal: string;
  position_size: number | null;
  original_pnl: number;
  normalized_pnl: number;
  cumulative_pnl: number;
}

interface MergedTradingHistoryTableProps {
  data: MergedTradeRow[];
  title?: string;
}

export const MergedTradingHistoryTable: React.FC<MergedTradingHistoryTableProps> = ({
  data,
  title = "Merged Trading History"
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  const totalPages = Math.ceil(data.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const paginatedData = data.slice(startIndex, startIndex + pageSize);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString;
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      });
    } catch {
      return dateString;
    }
  };

  const handleExportCSV = () => {
    const headers = ['Date/Time', 'Bot Name', 'Signal', 'Position Size', 'Original P&L', 'Normalized P&L', 'Cumulative P&L'];
    const csvContent = [
      headers.join(','),
      ...data.map(row => [
        `"${row.timestamp}"`,
        `"${row.bot_name}"`,
        `"${row.signal}"`,
        row.position_size !== null ? row.position_size : '—',
        row.original_pnl,
        row.normalized_pnl,
        row.cumulative_pnl
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', 'merged_trading_history.csv');
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  if (data.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{title}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-muted-foreground">
            No trading history data available. Run analysis first to see results.
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>{title}</CardTitle>
          <Button
            variant="outline"
            size="sm"
            onClick={handleExportCSV}
            className="flex items-center gap-2"
          >
            <Download className="h-4 w-4" />
            Export CSV
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <TablePagination
            currentPage={currentPage}
            pageSize={pageSize}
            totalItems={data.length}
            onPageChange={setCurrentPage}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setCurrentPage(1);
            }}
          />

          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date/Time</TableHead>
                  <TableHead>Bot Name</TableHead>
                  <TableHead>Signal</TableHead>
                  <TableHead className="text-right">Position Size</TableHead>
                  <TableHead className="text-right">Original P&L</TableHead>
                  <TableHead className="text-right">Normalized P&L</TableHead>
                  <TableHead className="text-right">Cumulative P&L</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedData.map((row, index) => (
                  <TableRow key={`${row.timestamp}-${row.bot_name}-${index}`}>
                    <TableCell className="font-mono text-xs">
                      {formatDate(row.timestamp)}
                    </TableCell>
                    <TableCell className="font-medium">
                      {row.bot_name}
                    </TableCell>
                    <TableCell>
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        row.signal.toLowerCase().includes('buy') || row.signal.toLowerCase().includes('long')
                          ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'
                          : row.signal.toLowerCase().includes('sell') || row.signal.toLowerCase().includes('short')
                          ? 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400'
                          : 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400'
                      }`}>
                        {row.signal}
                      </span>
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      {row.position_size !== null ? row.position_size.toFixed(4) : '—'}
                    </TableCell>
                    <TableCell className={`text-right font-mono font-medium ${
                      row.original_pnl > 0 
                        ? 'text-green-600 dark:text-green-400' 
                        : row.original_pnl < 0 
                        ? 'text-red-600 dark:text-red-400' 
                        : 'text-muted-foreground'
                    }`}>
                      {formatCurrency(row.original_pnl)}
                    </TableCell>
                    <TableCell className={`text-right font-mono font-medium ${
                      row.normalized_pnl > 0 
                        ? 'text-green-600 dark:text-green-400' 
                        : row.normalized_pnl < 0 
                        ? 'text-red-600 dark:text-red-400' 
                        : 'text-muted-foreground'
                    }`}>
                      {formatCurrency(row.normalized_pnl)}
                    </TableCell>
                    <TableCell className={`text-right font-mono font-bold ${
                      row.cumulative_pnl > 0 
                        ? 'text-green-600 dark:text-green-400' 
                        : row.cumulative_pnl < 0 
                        ? 'text-red-600 dark:text-red-400' 
                        : 'text-muted-foreground'
                    }`}>
                      {formatCurrency(row.cumulative_pnl)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          <TablePagination
            currentPage={currentPage}
            pageSize={pageSize}
            totalItems={data.length}
            onPageChange={setCurrentPage}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setCurrentPage(1);
            }}
          />
        </div>
      </CardContent>
    </Card>
  );
};